/**
 * The IndexedDB schema, and the one place that knows whether storage works at all.
 *
 * ## Why IndexedDB (ADR 8)
 *
 * Sessions accumulate nodes, branch sets and the raw chroma vectors kept for re-scoring
 * corrections; sketches can carry an audio `Blob`. That is structured data measured in
 * megabytes, wanted back by index, and written while an audio worklet is running.
 * `localStorage` is a synchronous 5 MB string store — the wrong shape, the wrong size,
 * and on the wrong thread.
 *
 * ## Why there is a driver interface in front of it
 *
 * Two reasons, both load-bearing:
 *
 *  1. **Private browsing.** Safari and Firefox in private mode either refuse to open a
 *     database or throw on the first write. The app must still run — a session you cannot
 *     save is enormously better than a session you cannot have — so a failure at any point
 *     degrades to an in-memory driver and flips `storageStatus().durable` to false. The UI
 *     watches that and shows its "nothing will be saved" chip.
 *  2. **Tests.** The same in-memory driver is a complete, fast, dependency-free fake, so
 *     `queries.ts` is exercised end to end under Node with no browser and no polyfill.
 *
 * ## Ownership and versioning
 *
 * This file owns the whole schema. `DB_VERSION` is 2; the upgrade path is a run of
 * ascending `if (oldVersion < n)` guards, so adding a store is a new guard, not a
 * rewrite. Version 2 folded in the tuning stores, which used to be a second database of
 * their own — see `adoptLegacyTuningDatabase` below for the one-time move of anybody's
 * saved profiles.
 */

import { deleteDB, openDB, type IDBPDatabase } from 'idb';
import type {
  CanonicalChord,
  PitchClass,
  RecognizerParams,
  SessionNode,
  SessionState,
} from '@hc/shared';
import type { PreferenceHints } from '@hc/shared';

export const DB_NAME = 'harmony-companion';

/**
 * Bump this, add a `case` to `upgrade`, and never edit an existing case: an upgrade that
 * has already run on someone's iPad cannot be changed, only followed.
 */
export const DB_VERSION = 2;

/** The sketchbook's own stores: sessions, sketches, preferences, the correction log. */
export type SketchbookStoreName = 'sessionSnapshot' | 'sketches' | 'preferences' | 'correctionLog';

/** The tuning screen's stores, owned by `tuning/profiles.ts` and merged in at version 2. */
export type TuningStoreName = 'tuningProfiles' | 'tuningMeta';

export type StoreName = SketchbookStoreName | TuningStoreName;

/**
 * The sketchbook stores — deliberately *not* every store.
 *
 * `clearAllStores` iterates this, and "start fresh" must not throw away an hour of
 * tuning at a piano along with a handful of sketches. Use `ALL_STORE_NAMES` for anything
 * that means the whole schema.
 */
export const STORE_NAMES: readonly SketchbookStoreName[] = [
  'sessionSnapshot',
  'sketches',
  'preferences',
  'correctionLog',
];

export const TUNING_STORE_NAMES: readonly TuningStoreName[] = ['tuningProfiles', 'tuningMeta'];

/** Every store in the database, in creation order. */
export const ALL_STORE_NAMES: readonly StoreName[] = [...STORE_NAMES, ...TUNING_STORE_NAMES];

/**
 * The database the tuning stores used to live in, before version 2 folded them in here.
 *
 * Kept only so an install from an earlier deploy can be found and emptied exactly once.
 * Nothing writes to it.
 */
export const LEGACY_TUNING_DB_NAME = 'hc-tuning';

// ---------------------------------------------------------------------------
// Records
// ---------------------------------------------------------------------------

/** One autosaved session, keyed by its own id. Refresh recovery reads the newest. */
export interface SessionSnapshotRecord {
  sessionId: string;
  savedAt: number;
  /** The full reducer state. Restored via the `RESTORED` action after validation. */
  state: SessionState;
}

/** A transition the player marked as one they liked, within a sketch. */
export interface FavouriteTransition {
  fromNodeId: string;
  toNodeId: string;
  fromPcs: PitchClass[];
  toPcs: PitchClass[];
  note?: string;
  markedAt: number;
}

/** A saved piece of explanation — the model's, or the player's own. */
export interface SketchExplanation {
  /** The node it belongs to, when it is about one. */
  nodeId?: string;
  /** The branch it belongs to, when it is about one. */
  suggestionId?: string;
  question?: string;
  text: string;
  createdAt: number;
}

/** A kept session: the thing the sketchbook lists. */
export interface SketchRecord {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  /** The session it was captured from, when it came from one. */
  sessionId?: string;
  nodes: SessionNode[];
  intention?: string;
  userNotes: string;
  tags: string[];
  favouriteTransitions: FavouriteTransition[];
  explanations: SketchExplanation[];
  /** Optional recording of the session. Stored as a Blob — this is why not localStorage. */
  audio?: Blob;
  audioMimeType?: string;
  audioDurationMs?: number;
}

/** Preference records. Two keys: the hints themselves, and the private tallies behind them. */
export const PREFERENCES_KEY = 'singleton';
export const PREFERENCE_TALLIES_KEY = 'tallies';

/**
 * Whether the player is harvesting labelled recognitions. A device preference, in the
 * `preferences` store under its own out-of-line key — exactly as `state/modePreference.ts`
 * argues, an added key needs no `createObjectStore` and therefore no `DB_VERSION` bump.
 */
export const COLLECT_RECOGNITIONS_KEY = 'collectRecognitions';

/**
 * The settings the host holds that outlive a session: sound On the Go, the model tier, and
 * whether the prompts are on show. One record rather than three, because they are read
 * together on every mount and a settings sheet gains a toggle far more often than the
 * storage layer wants a new key. Same store, same no-bump argument as above.
 */
export const HOST_SETTINGS_KEY = 'hostSettings';

/**
 * Counters that back `PreferenceHints` but are not part of its frozen shape — the key
 * histogram that `commonKeys` is ordered by, and the denominator behind
 * `explanationTapRate`.
 */
export interface PreferenceTallies {
  keyCounts: Record<string, number>;
  explanationsOffered: number;
  explanationsOpened: number;
  sessionCount: number;
}

/**
 * What the player said about a recognition.
 *
 * A corrections-only log cannot be trained on, because silence in it is ambiguous: it
 * means either "right" or "wrong and nobody noticed", and those are opposite facts.
 * Recording all three answers is what turns the log into a labelled dataset.
 *
 * `'unattended'` is deliberately a verdict rather than an absence. The player was playing,
 * not grading; a guess nobody looked at is honest evidence about attention and weak
 * evidence about the recogniser, and writing it down is the only way a consumer can tell
 * the difference between that and a confirmation.
 */
export type RecognitionVerdict = 'corrected' | 'confirmed' | 'unattended';

/**
 * One verdict on one recognition, kept for tuning and for later calibration.
 *
 * Written to the `correctionLog` store, which predates the other two verdicts — the store
 * name is unchanged on purpose, because renaming it would mean a `DB_VERSION` bump and a
 * migration for a cosmetic gain. Records are schemaless values, so the new fields simply
 * appear on new records; see `verdictOf` for how old ones are read.
 *
 * Capped: `CORRECTION_LOG_CAP` while collection is off (a rolling diagnostic window, and
 * these hold 12-bin chroma vectors), `VERDICT_LOG_CAP` while it is on.
 */
export interface CorrectionLogEntry {
  /** Assigned by the store. */
  id?: number;
  chromaIntegrated: number[];
  /** What the recogniser emitted. */
  emitted: CanonicalChord;
  /** What the player said it actually was. Only ever set for `verdict: 'corrected'`. */
  corrected?: CanonicalChord;
  /** The parameters in force at the time, so a bad setting is visible in hindsight. */
  params: RecognizerParams;
  ts: number;
  /**
   * Absent on records written before collection mode existed. Every one of those was a
   * correction, so `verdictOf` reads an absent field as `'corrected'` rather than
   * guessing — this is the one field a migration would have had to invent, and it does
   * not have to be invented.
   */
  verdict?: RecognitionVerdict;
  /**
   * `DetectedChord.confidence` at emission.
   *
   * The field that makes an unattended record interpretable: a confident guess nobody
   * queried says something quite different from an uncertain one nobody queried, and
   * without it the two are the same row.
   */
  confidence?: number;
  nodeId?: string;
  sessionId?: string;
}

/** The verdict a record carries, defaulting old records to what they all were. */
export function verdictOf(entry: CorrectionLogEntry): RecognitionVerdict {
  return entry.verdict ?? 'corrected';
}

/** The rolling debug window, in force whenever collection mode is off. */
export const CORRECTION_LOG_CAP = 200;

/**
 * The ceiling while collection mode is on.
 *
 * Not a target: a safety rail so a forgotten toggle cannot fill a device. At roughly
 * 1 kB a record — a 12-bin chroma, two chords and a parameter set — this is about 20 MB,
 * and at a realistic few hundred chords an hour it is many sittings' worth of harvest.
 * Eviction is oldest-first and never anything else.
 */
export const VERDICT_LOG_CAP = 20_000;

// ---------------------------------------------------------------------------
// Store configuration — shared by the real driver and the in-memory one
// ---------------------------------------------------------------------------

type IndexExtractor = (value: never) => IDBValidKey | IDBValidKey[] | undefined;

interface StoreConfig {
  /** `null` means out-of-line keys: the caller supplies them. */
  keyPath: string | null;
  autoIncrement: boolean;
  indexes: Record<string, { keyPath: string; multiEntry: boolean; extract: IndexExtractor }>;
}

function index(keyPath: string, multiEntry = false): StoreConfig['indexes'][string] {
  return {
    keyPath,
    multiEntry,
    extract: ((value: Record<string, unknown>) =>
      value[keyPath] as IDBValidKey | IDBValidKey[] | undefined) as IndexExtractor,
  };
}

export const STORE_CONFIG: Readonly<Record<StoreName, StoreConfig>> = Object.freeze({
  sessionSnapshot: {
    keyPath: 'sessionId',
    autoIncrement: false,
    indexes: { 'by-savedAt': index('savedAt') },
  },
  sketches: {
    keyPath: 'id',
    autoIncrement: false,
    indexes: {
      'by-createdAt': index('createdAt'),
      'by-updatedAt': index('updatedAt'),
      'by-tag': index('tags', true),
    },
  },
  preferences: { keyPath: null, autoIncrement: false, indexes: {} },
  correctionLog: {
    keyPath: 'id',
    autoIncrement: true,
    indexes: { 'by-ts': index('ts') },
  },
  // The record shapes are `TuningProfile` and `{ key, value }` from `tuning/profiles.ts`.
  // They are not imported here: this file is the bottom of the stack and the tuning
  // module depends on it, not the other way round.
  tuningProfiles: { keyPath: 'id', autoIncrement: false, indexes: {} },
  tuningMeta: { keyPath: 'key', autoIncrement: false, indexes: {} },
});

// ---------------------------------------------------------------------------
// The driver
// ---------------------------------------------------------------------------

/** One write in a multi-store transaction. */
export interface WriteOp {
  store: StoreName;
  value: unknown;
  /** Only for out-of-line stores (`preferences`). */
  key?: IDBValidKey;
}

/**
 * Everything `queries.ts` is allowed to do to storage. Deliberately small: no cursors, no
 * key ranges, no versioning — anything that cannot be expressed here belongs in a schema
 * change rather than in a caller.
 */
export interface StorageDriver {
  /** False for the in-memory fallback: nothing written will outlive the tab. */
  readonly durable: boolean;
  get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined>;
  getAll<T>(store: StoreName): Promise<T[]>;
  /** Ascending by the index's key; pass `descending` for newest-first listings. */
  getAllFromIndex<T>(store: StoreName, indexName: string, descending?: boolean): Promise<T[]>;
  keys(store: StoreName): Promise<IDBValidKey[]>;
  put<T>(store: StoreName, value: T, key?: IDBValidKey): Promise<IDBValidKey>;
  /** All-or-nothing across stores. The reason this is IndexedDB and not a key-value shim. */
  putMany(ops: readonly WriteOp[]): Promise<void>;
  delete(store: StoreName, key: IDBValidKey): Promise<void>;
  deleteMany(store: StoreName, keys: readonly IDBValidKey[]): Promise<void>;
  clear(store: StoreName): Promise<void>;
  count(store: StoreName): Promise<number>;
  close(): void;
}

// ---------------------------------------------------------------------------
// In-memory driver — the private-browsing fallback and the test double
// ---------------------------------------------------------------------------

function compareKeys(a: IDBValidKey, b: IDBValidKey): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
}

/**
 * A complete implementation of `StorageDriver` over plain `Map`s.
 *
 * Exported so tests can construct one directly, and so the fallback path is the same code
 * that the test suite exercises rather than a second, unloved implementation.
 */
export function createMemoryDriver(): StorageDriver {
  const stores = new Map<StoreName, Map<IDBValidKey, unknown>>();
  const sequences = new Map<StoreName, number>();
  for (const name of ALL_STORE_NAMES) {
    stores.set(name, new Map());
    sequences.set(name, 0);
  }

  function tableOf(store: StoreName): Map<IDBValidKey, unknown> {
    return stores.get(store) as Map<IDBValidKey, unknown>;
  }

  function write(op: WriteOp): IDBValidKey {
    const config = STORE_CONFIG[op.store];
    let key = op.key;
    if (config.keyPath) {
      const record = op.value as Record<string, unknown>;
      const existing = record[config.keyPath] as IDBValidKey | undefined;
      if (existing === undefined || existing === null) {
        if (!config.autoIncrement) {
          throw new Error(`${op.store} requires a "${config.keyPath}" on the record`);
        }
        const next = (sequences.get(op.store) ?? 0) + 1;
        sequences.set(op.store, next);
        record[config.keyPath] = next;
        key = next;
      } else {
        key = existing;
        if (config.autoIncrement && typeof existing === 'number') {
          sequences.set(op.store, Math.max(sequences.get(op.store) ?? 0, existing));
        }
      }
    }
    if (key === undefined) throw new Error(`${op.store} needs an explicit key`);
    // IndexedDB stores a structured clone, not a reference. Emulating that is what makes
    // "I mutated the object I saved and the database changed" impossible here too.
    tableOf(op.store).set(key, clone(op.value));
    return key;
  }

  return {
    durable: false,
    async get<T>(store: StoreName, key: IDBValidKey) {
      return clone(tableOf(store).get(key)) as T | undefined;
    },
    async getAll<T>(store: StoreName) {
      return [...tableOf(store).entries()]
        .sort((a, b) => compareKeys(a[0], b[0]))
        .map(([, value]) => clone(value)) as T[];
    },
    async getAllFromIndex<T>(store: StoreName, indexName: string, descending = false) {
      const definition = STORE_CONFIG[store].indexes[indexName];
      if (!definition) throw new Error(`no index "${indexName}" on ${store}`);
      const rows = [...tableOf(store).values()].map((value) => ({
        value,
        key: definition.extract(value as never),
      }));
      // A multiEntry index skips records whose key is an empty array, exactly as IDB does.
      const present = rows.filter(
        (row) => row.key !== undefined && (!Array.isArray(row.key) || row.key.length > 0),
      );
      present.sort((a, b) =>
        compareKeys(
          (Array.isArray(a.key) ? (a.key[0] as IDBValidKey) : a.key) as IDBValidKey,
          (Array.isArray(b.key) ? (b.key[0] as IDBValidKey) : b.key) as IDBValidKey,
        ),
      );
      if (descending) present.reverse();
      return present.map((row) => clone(row.value)) as T[];
    },
    async keys(store: StoreName) {
      return [...tableOf(store).keys()].sort(compareKeys);
    },
    async put<T>(store: StoreName, value: T, key?: IDBValidKey) {
      return write({ store, value, ...(key === undefined ? {} : { key }) });
    },
    async putMany(ops: readonly WriteOp[]) {
      // Atomicity: stage every write, and only commit if all of them are legal.
      const staged = ops.map((op) => ({ op, value: clone(op.value) }));
      for (const entry of staged) write({ ...entry.op, value: entry.value });
    },
    async delete(store: StoreName, key: IDBValidKey) {
      tableOf(store).delete(key);
    },
    async deleteMany(store: StoreName, keys: readonly IDBValidKey[]) {
      for (const key of keys) tableOf(store).delete(key);
    },
    async clear(store: StoreName) {
      tableOf(store).clear();
    },
    async count(store: StoreName) {
      return tableOf(store).size;
    },
    close() {
      /* nothing to close */
    },
  };
}

/** `structuredClone` where it exists, JSON otherwise — and Blobs are passed through. */
function clone<T>(value: T): T {
  if (value === undefined || value === null) return value;
  if (typeof globalThis.structuredClone === 'function') {
    try {
      return globalThis.structuredClone(value);
    } catch {
      /* fall through — a value structuredClone refuses is not one IDB would accept either */
    }
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

// ---------------------------------------------------------------------------
// IndexedDB driver
// ---------------------------------------------------------------------------

/** The subset of `IDBPDatabase` used here, so nothing needs `any` to talk to it. */
interface UntypedDb {
  get(store: string, key: IDBValidKey): Promise<unknown>;
  getAll(store: string): Promise<unknown[]>;
  getAllKeys(store: string): Promise<IDBValidKey[]>;
  getAllFromIndex(store: string, index: string): Promise<unknown[]>;
  put(store: string, value: unknown, key?: IDBValidKey): Promise<IDBValidKey>;
  delete(store: string, key: IDBValidKey): Promise<void>;
  clear(store: string): Promise<void>;
  count(store: string): Promise<number>;
  transaction(
    stores: readonly string[],
    mode: 'readwrite',
  ): {
    objectStore(name: string): {
      put(value: unknown, key?: IDBValidKey): Promise<IDBValidKey>;
      delete(key: IDBValidKey): Promise<void>;
    };
    done: Promise<void>;
  };
  close(): void;
}

function createIdbDriver(db: UntypedDb, onFailure: (error: unknown) => void): StorageDriver {
  /**
   * Every operation is wrapped: a quota error, a private-mode refusal or a database
   * deleted from under us degrades the whole app to in-memory rather than throwing into a
   * component. Callers get a resolved promise and a false `durable` flag.
   */
  function guard<T>(run: () => Promise<T>, fallback: () => Promise<T>): Promise<T> {
    return run().catch((error: unknown) => {
      onFailure(error);
      return fallback();
    });
  }

  return {
    durable: true,
    get: (store, key) =>
      guard(
        () => db.get(store, key) as never,
        async () => undefined as never,
      ),
    getAll: (store) =>
      guard(
        () => db.getAll(store) as never,
        async () => [] as never,
      ),
    getAllFromIndex: (store, indexName, descending = false) =>
      guard(
        async () => {
          const rows = await db.getAllFromIndex(store, indexName);
          return (descending ? rows.reverse() : rows) as never;
        },
        async () => [] as never,
      ),
    keys: (store) =>
      guard(
        () => db.getAllKeys(store),
        async () => [],
      ),
    put: (store, value, key) =>
      guard(
        () => db.put(store, value, key),
        async () => (key ?? 0) as IDBValidKey,
      ),
    putMany: (ops) =>
      guard(
        async () => {
          const names = [...new Set(ops.map((op) => op.store))];
          const tx = db.transaction(names, 'readwrite');
          await Promise.all(ops.map((op) => tx.objectStore(op.store).put(op.value, op.key)));
          await tx.done;
        },
        async () => undefined,
      ),
    delete: (store, key) =>
      guard(
        () => db.delete(store, key),
        async () => undefined,
      ),
    deleteMany: (store, keys) =>
      guard(
        async () => {
          const tx = db.transaction([store], 'readwrite');
          await Promise.all(keys.map((key) => tx.objectStore(store).delete(key)));
          await tx.done;
        },
        async () => undefined,
      ),
    clear: (store) =>
      guard(
        () => db.clear(store),
        async () => undefined,
      ),
    count: (store) =>
      guard(
        () => db.count(store),
        async () => 0,
      ),
    close: () => {
      try {
        db.close();
      } catch {
        /* already gone */
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Opening, and the durability signal the UI shows
// ---------------------------------------------------------------------------

export type StorageUnavailableReason =
  'none' | 'no-indexeddb' | 'open-failed' | 'open-timed-out' | 'write-failed';

export interface StorageStatus {
  /** False means the in-memory fallback is in use and nothing survives a refresh. */
  durable: boolean;
  reason: StorageUnavailableReason;
  /** Set when a specific failure was observed, for the diagnostics screen. */
  message?: string;
}

let status: StorageStatus = { durable: true, reason: 'none' };
const statusListeners = new Set<(status: StorageStatus) => void>();

export function storageStatus(): StorageStatus {
  return status;
}

/** The UI's "nothing will be saved" chip subscribes here. */
export function subscribeToStorageStatus(listener: (status: StorageStatus) => void): () => void {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}

function setStatus(next: StorageStatus): void {
  if (status.durable === next.durable && status.reason === next.reason) return;
  status = next;
  for (const listener of statusListeners) listener(status);
}

/** How long to wait for `openDB` before giving up. Some private modes simply never answer. */
export const DB_OPEN_TIMEOUT_MS = 4_000;

let driverPromise: Promise<StorageDriver> | null = null;
let memoryFallback: StorageDriver | null = null;

function degrade(reason: StorageUnavailableReason, error?: unknown): StorageDriver {
  if (!memoryFallback) memoryFallback = createMemoryDriver();
  setStatus({
    durable: false,
    reason,
    ...(error === undefined ? {} : { message: String((error as Error)?.message ?? error) }),
  });
  return memoryFallback;
}

function hasIndexedDb(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    // Accessing `indexedDB` itself throws in some locked-down configurations.
    return false;
  }
}

/** Just enough of `IDBPDatabase` to build a store, so no `any` is needed to do it. */
interface UpgradingDb {
  createObjectStore(
    name: string,
    options?: IDBObjectStoreParameters,
  ): { createIndex(name: string, keyPath: string, options?: IDBIndexParameters): unknown };
}

/** Create one store and its indexes. Shared by every `case` of the upgrade. */
function createStore(db: UpgradingDb, name: StoreName): void {
  const config = STORE_CONFIG[name];
  const store = db.createObjectStore(name, {
    ...(config.keyPath ? { keyPath: config.keyPath } : {}),
    autoIncrement: config.autoIncrement,
  });
  for (const [indexName, definition] of Object.entries(config.indexes)) {
    store.createIndex(indexName, definition.keyPath, { multiEntry: definition.multiEntry });
  }
}

/**
 * Move a pre-version-2 `hc-tuning` database into the tuning stores, then delete it.
 *
 * Runs once, immediately after the upgrade that created those stores, and never again —
 * an install that has already reached version 2 does not call this. A profile is the
 * output of an hour at a real piano with eighteen sliders, so losing one to a schema
 * tidy-up would be the worst possible trade; equally, a fresh install must not be slowed
 * or broken by looking for a database that was never there.
 *
 * Existence is detected by opening `hc-tuning` **without a version**: if that fires
 * `upgradeneeded`, the database did not exist and we have just created an empty one, which
 * is then deleted again. `indexedDB.databases()` would be the direct question, but it is
 * too recent to rely on.
 *
 * Every failure is swallowed. The worst case is that the old database lingers unread,
 * which costs a few kilobytes and nothing else.
 */
async function adoptLegacyTuningDatabase(driver: StorageDriver): Promise<void> {
  let existed = true;
  let legacy: IDBPDatabase | null = null;
  try {
    legacy = await openDB(LEGACY_TUNING_DB_NAME, undefined, {
      upgrade() {
        existed = false;
      },
    });
    if (existed) {
      for (const name of TUNING_STORE_NAMES) {
        if (!legacy.objectStoreNames.contains(name)) continue;
        for (const record of await legacy.getAll(name)) await driver.put(name, record);
      }
    }
  } catch (error) {
    console.warn('[hc] could not read the old tuning database; leaving it alone.', error);
    legacy?.close();
    return;
  }
  legacy.close();
  // Not awaited: another tab holding the old database open would block the delete
  // indefinitely, and nothing here depends on it having finished.
  void deleteDB(LEGACY_TUNING_DB_NAME).catch(() => {
    /* it will be found again by the next install that upgrades */
  });
}

async function open(): Promise<StorageDriver> {
  if (!hasIndexedDb()) return degrade('no-indexeddb');

  let timer: ReturnType<typeof setTimeout> | undefined;
  // A box rather than a bare `let`: it is written inside a callback, and this way its
  // value after the await is obviously not the initial one.
  const upgraded = { from: -1 };
  try {
    const opening = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        upgraded.from = oldVersion;
        // One ascending guard per version, all of them run in order: a database at
        // version 0 gets every store, one at version 1 gets only what version 2 added.
        // Adding a store is a new guard, never an edit to an existing one — an upgrade
        // that has already run on someone's iPad cannot be changed, only followed.
        if (oldVersion < 1) {
          for (const name of STORE_NAMES) createStore(db, name);
        }
        if (oldVersion < 2) {
          // Version 2: the tuning stores, previously a database of their own.
          for (const name of TUNING_STORE_NAMES) createStore(db, name);
        }
      },
      blocking() {
        // Another tab wants to upgrade. Let go rather than deadlock it.
        driverPromise = null;
      },
    });

    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('IndexedDB did not open')), DB_OPEN_TIMEOUT_MS);
    });

    const db = (await Promise.race([opening, timeout])) as unknown as UntypedDb;
    setStatus({ durable: true, reason: 'none' });
    const driver = createIdbDriver(db, (error) => degrade('write-failed', error));
    // Awaited, so the first `loadActiveParams()` of the upgrade run already sees the
    // profiles it is looking for rather than the defaults.
    if (upgraded.from >= 0 && upgraded.from < 2) await adoptLegacyTuningDatabase(driver);
    return driver;
  } catch (error) {
    return degrade(
      String((error as Error)?.message ?? '').includes('did not open')
        ? 'open-timed-out'
        : 'open-failed',
      error,
    );
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * The database. Opened once and shared; every caller awaits the same promise.
 *
 * Never rejects. If storage is unavailable this resolves to the in-memory driver and
 * `storageStatus().durable` becomes false — the app keeps working, honestly.
 */
export function getDb(): Promise<StorageDriver> {
  if (!driverPromise) driverPromise = open();
  return driverPromise;
}

/**
 * Replace the database with a specific driver. For tests, and for the "start fresh"
 * affordance in settings.
 */
export function setDb(driver: StorageDriver | null): void {
  if (driverPromise && driver === null) {
    void driverPromise.then((existing) => existing.close());
  }
  driverPromise = driver ? Promise.resolve(driver) : null;
  memoryFallback = null;
  if (driver)
    setStatus({ durable: driver.durable, reason: driver.durable ? 'none' : 'no-indexeddb' });
  else status = { durable: true, reason: 'none' };
}

/** Convenience for tests and for `Storage cleared` in settings. */
export async function clearAllStores(): Promise<void> {
  const db = await getDb();
  for (const name of STORE_NAMES) await db.clear(name);
}

/** Re-exported so callers do not have to import the shared package for this one type. */
export type { PreferenceHints };
